import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RotateCcw, Shuffle, Check, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SECTIONS, buildAllQuestions } from "@/data/questions";
import { loadCustomQuestions } from "@/lib/customQuestions";
import {
  loadProgress,
  toggleMastered,
  isMastered,
  countMastered,
} from "@/lib/progress";
import { loadNotes, getNote } from "@/lib/notes";
import AITerminal from "@/components/AITerminal";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function Flashcards() {
  const [progress, setProgress] = useState({});
  const [customQs, setCustomQs] = useState([]);
  const [notes, setNotes] = useState({});
  const [section, setSection] = useState("all");
  const [order, setOrder] = useState([]);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [showAI, setShowAI] = useState(false);

  useEffect(() => {
    setProgress(loadProgress());
    setCustomQs(loadCustomQuestions());
    setNotes(loadNotes());
  }, []);

  const allQuestions = useMemo(() => buildAllQuestions(customQs), [customQs]);

  const pool = useMemo(() => {
    if (section === "all") return allQuestions;
    return allQuestions.filter((q) => q.sectionId === section);
  }, [section, allQuestions]);

  useEffect(() => {
    setOrder(shuffle(pool.map((q) => q.id)));
    setIdx(0);
    setFlipped(false);
    setShowAI(false);
  }, [pool]);

  const currentId = order[idx];
  const current = pool.find((q) => q.id === currentId);

  const next = () => {
    setFlipped(false);
    setShowAI(false);
    setIdx((i) => (i + 1) % order.length);
  };
  const prev = () => {
    setFlipped(false);
    setShowAI(false);
    setIdx((i) => (i - 1 + order.length) % order.length);
  };
  const reshuffle = () => {
    setOrder(shuffle(pool.map((q) => q.id)));
    setIdx(0);
    setFlipped(false);
    setShowAI(false);
  };
  const onMaster = () => {
    if (!current) return;
    setProgress(toggleMastered(progress, current.id));
  };

  // keyboard nav
  useEffect(() => {
    const h = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
      if (e.key === " ") {
        e.preventDefault();
        setFlipped((v) => !v);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, idx]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 bg-grid">
      <header className="sticky top-0 z-20 bg-zinc-950/85 backdrop-blur border-b border-zinc-800">
        <div className="px-5 md:px-8 py-4 flex items-center gap-4 flex-wrap">
          <Link
            to="/"
            data-testid="back-home"
            className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-sky-400 transition-colors uppercase tracking-widest"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Link>
          <h1 className="font-display text-lg md:text-xl font-bold">
            Mode Flashcards
          </h1>
          <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            espace = retourner · ← → = naviguer
          </span>
          <div className="flex-1" />
          <Select value={section} onValueChange={setSection}>
            <SelectTrigger
              data-testid="flashcards-section-select"
              className="bg-zinc-900 border-zinc-800 rounded-none h-10 w-[260px] text-sm"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les sections</SelectItem>
              {SECTIONS.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            data-testid="reshuffle-btn"
            onClick={reshuffle}
            className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-zinc-600 px-3 py-2 text-xs uppercase tracking-widest text-zinc-300 transition-colors"
          >
            <Shuffle className="w-3.5 h-3.5" />
            Mélanger
          </button>
        </div>
      </header>

      <main className="px-5 md:px-8 py-8 max-w-4xl mx-auto">
        {!current ? (
          <div className="text-center text-zinc-500">Aucune question.</div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4 text-xs uppercase tracking-widest text-zinc-500">
              <span data-testid="card-counter">
                {idx + 1} / {order.length}
              </span>
              <span>
                {countMastered(progress)} maîtrisée
                {countMastered(progress) > 1 ? "s" : ""} au total
              </span>
            </div>

            <div
              className="flip-card cursor-pointer select-none"
              style={{ minHeight: 320 }}
              onClick={() => setFlipped((v) => !v)}
              data-testid="flashcard"
            >
              <div className={`flip-card-inner ${flipped ? "flipped" : ""}`}>
                {/* Front */}
                <div className="flip-face bg-zinc-900 border border-zinc-800 px-6 md:px-10 py-10 md:py-14 min-h-[320px] flex flex-col justify-between">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-sky-400 font-bold">
                      {current.sectionShort}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      {current.groupTitle}
                    </span>
                  </div>
                  <div className="my-6">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-600 mb-3">
                      Question
                    </div>
                    <p className="font-display text-2xl md:text-3xl leading-snug">
                      {current.text}
                    </p>
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-600">
                    Cliquez ou appuyez sur espace pour générer la réponse →
                  </div>
                </div>

                {/* Back */}
                <div className="flip-face flip-back absolute inset-0 bg-zinc-900 border border-sky-500/40 px-6 md:px-10 py-8 min-h-[320px] flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-sky-400 font-bold">
                      Réponse modèle
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      {current.sectionShort}
                    </span>
                  </div>
                  <p className="font-display text-base md:text-lg text-zinc-200 leading-snug mb-2">
                    {current.text}
                  </p>
                  <div onClick={(e) => e.stopPropagation()}>
                    {showAI ? (
                      <AITerminal
                        question={current.text}
                        questionId={current.id}
                        sectionTitle={current.sectionTitle}
                        groupTitle={current.groupTitle}
                        note={getNote(notes, current.id)}
                        context={`${current.sectionTitle} — ${current.groupTitle}`}
                        onClose={() => setShowAI(false)}
                      />
                    ) : (
                      <button
                        data-testid="flashcard-generate-ai"
                        onClick={() => setShowAI(true)}
                        className="mt-4 inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-zinc-950 font-bold px-4 py-2 text-sm transition-colors uppercase tracking-wider"
                      >
                        <Sparkles className="w-4 h-4" />
                        Générer la réponse IA
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex gap-2">
                <button
                  data-testid="prev-card"
                  onClick={prev}
                  className="inline-flex items-center gap-1 border border-zinc-800 hover:border-zinc-600 px-4 py-2 text-sm uppercase tracking-widest text-zinc-300 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Précédent
                </button>
                <button
                  data-testid="flip-card-btn"
                  onClick={() => setFlipped((v) => !v)}
                  className="inline-flex items-center gap-1 border border-zinc-800 hover:border-zinc-600 px-4 py-2 text-sm uppercase tracking-widest text-zinc-300 transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Retourner
                </button>
                <button
                  data-testid="next-card"
                  onClick={next}
                  className="inline-flex items-center gap-1 bg-sky-500 hover:bg-sky-400 text-zinc-950 font-bold px-4 py-2 text-sm uppercase tracking-widest transition-colors"
                >
                  Suivant
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              <button
                data-testid="flashcard-master-toggle"
                onClick={onMaster}
                className={`inline-flex items-center gap-2 border px-4 py-2 text-sm transition-colors uppercase tracking-widest ${
                  isMastered(progress, current.id)
                    ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/20"
                    : "bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-zinc-500"
                }`}
              >
                <Check className="w-4 h-4" />
                {isMastered(progress, current.id)
                  ? "Maîtrisée"
                  : "Marquer maîtrisée"}
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
